import React, { useState, useEffect, useRef } from "react";
import "./GuestbookSection.css";
import {
  getGuestbookEntries,
  addGuestbookEntry,
  GuestbookEntry as SupabaseGuestbookEntry,
} from "../supabase";

interface GuestbookEntry {
  id: string;
  name: string;
  message: string;
  side: "groom" | "bride" | "none";
  createdAt: string;
}

interface NewGuestbookEntry {
  name: string;
  message: string;
  side: "groom" | "bride" | "";
}

const GuestbookSection: React.FC = () => {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [newEntry, setNewEntry] = useState<NewGuestbookEntry>({
    name: "",
    message: "",
    side: "",
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const firstSegmentRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  const ENTRIES_PER_PAGE = 5;

  useEffect(() => {
    // Supabase에서 축하한마디 데이터 가져오기
    const fetchEntries = async () => {
      try {
        const supabaseEntries = await getGuestbookEntries();
        const formattedEntries: GuestbookEntry[] = supabaseEntries.map(
          (entry) => ({
            id: entry.id || "",
            name: entry.name,
            message: entry.message,
            side: entry.side,
            createdAt: entry.created_at || new Date().toISOString(),
          })
        );
        setEntries(formattedEntries);
      } catch (error) {
        console.error("Error fetching guestbook entries:", error);
        // 에러 발생 시 빈 배열로 설정
        setEntries([]);
      }
    };

    fetchEntries();
  }, []);

  // 모달이 열릴 때 body 스크롤 방지 및 닫힐 때 복원 (iOS 호환성 개선)
  useEffect(() => {
    if (showModal) {
      // 모달이 열릴 때 - iOS에서 입력 필드 문제를 피하기 위해 position: fixed 제거
      document.body.style.overflow = "hidden";
      // iOS에서 입력 필드 포커스 문제를 방지하기 위해 position: fixed 사용 안 함
    } else {
      // 모달이 닫힐 때
      document.body.style.overflow = "";
    }

    return () => {
      // 컴포넌트 언마운트 시 정리
      document.body.style.overflow = "";
    };
  }, [showModal]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showModal) {
        setShowModal(false);
        setNewEntry({ name: "", message: "", side: "" });
      }
    };

    if (showModal) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntry.name.trim() || !newEntry.message.trim()) {
      alert("이름과 메시지를 모두 입력해주세요.");
      return;
    }

    setLoading(true);
    try {
      // Supabase에 축하한마디 추가
      const supabaseEntry = await addGuestbookEntry({
        name: newEntry.name.trim(),
        message: newEntry.message.trim(),
        side: newEntry.side || ("none" as "groom" | "bride" | "none"),
      });

      // 새로운 엔트리를 로컬 상태에 추가
      const entry: GuestbookEntry = {
        id: supabaseEntry.id || "",
        name: supabaseEntry.name,
        message: supabaseEntry.message,
        side: supabaseEntry.side,
        createdAt: supabaseEntry.created_at || new Date().toISOString(),
      };

      setEntries((prev) => [entry, ...prev]);
      setNewEntry({ name: "", message: "", side: "" });
      setSubmitted(true);
      setShowModal(false);
      setCurrentPage(1); // 새 메시지 추가 시 첫 페이지로 이동
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error("Error submitting entry:", error);
      alert("메시지 전송에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 메시지 3줄 제한 (입력 시)
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lines = value.split("\n");
    if (lines.length > 3) {
      setNewEntry((prev) => ({
        ...prev,
        message: lines.slice(0, 3).join("\n"),
      }));
    } else {
      setNewEntry((prev) => ({ ...prev, message: value }));
    }
  };

  // 메시지 한 줄만 보이게 (리스트)
  const getFirstLine = (msg: string) => {
    const first = msg.split("\n")[0];
    return first.length > 40 ? first.slice(0, 40) + "..." : first;
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(entries.length / ENTRIES_PER_PAGE);
  const startIndex = (currentPage - 1) * ENTRIES_PER_PAGE;
  const endIndex = startIndex + ENTRIES_PER_PAGE;
  const currentEntries = entries.slice(startIndex, endIndex);

  // 페이지 변경 함수
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // 페이지 변경 시 축하메시지 섹션으로 부드럽게 스크롤
    const guestbookSection = document.querySelector(".guestbook-section");
    if (guestbookSection) {
      guestbookSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Segment control for groom/bride
  const segmentOptions = [
    { value: "groom", label: "신랑측", emoji: "🤵‍♂️" },
    { value: "bride", label: "신부측", emoji: "👰‍♀️" },
  ];

  return (
    <section className="guestbook-section">
      <div className="guestbook-header">
        <h2 className="section-title">축하한마디</h2>
        <p>따뜻한 메시지로 두 사람의 앞날을 축복해 주세요.</p>
      </div>

      <div
        className="guestbook-content"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
        }}
      >
        <button
          className="submit-btn"
          style={{ maxWidth: 300, marginBottom: 24 }}
          onClick={() => {
            setNewEntry({ name: "", message: "", side: "" });
            setShowModal(true);
          }}
        >
          축하한마디 남기기
        </button>
        {submitted && (
          <div className="success-message" style={{ maxWidth: 400 }}>
            축하 메시지가 성공적으로 전송되었습니다! 🎉
          </div>
        )}
        <div
          className="guestbook-entries"
          style={{ width: "100%", maxWidth: 600 }}
        >
          <div className="entries-container">
            {entries.length === 0 ? (
              <p className="no-entries">
                아직 축하 메시지가 없습니다.
                <br />첫 번째 메시지를 남겨보세요!
              </p>
            ) : (
              <>
                {currentEntries.map((entry) => (
                  <div key={entry.id} className={`entry ${entry.side}`}>
                    <div className="entry-row-compact">
                      {entry.side !== "none" && (
                        <span
                          className={`entry-side segment-btn small selected${
                            entry.side === "bride" ? " bride" : ""
                          }`}
                          style={{ marginRight: 6, pointerEvents: "none" }}
                        >
                          <span style={{ fontSize: "0.85rem", marginRight: 0 }}>
                            {entry.side === "groom" ? "🤵‍♂️" : "👰‍♀️"}
                          </span>
                          <span style={{ fontSize: "0.7rem", marginLeft: 1 }}>
                            {entry.side === "groom" ? "신랑측" : "신부측"}
                          </span>
                        </span>
                      )}
                      <span className="entry-name-compact">{entry.name}</span>
                    </div>
                    <div
                      className="entry-message-compact"
                      title={entry.message}
                    >
                      {getFirstLine(entry.message)}
                    </div>
                    <div className="entry-date" style={{ marginTop: 2 }}>
                      {formatDate(entry.createdAt)}
                    </div>
                  </div>
                ))}

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹ 이전
                    </button>

                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            className={`pagination-number ${
                              currentPage === page ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      다음 ›
                    </button>
                  </div>
                )}

                {/* 페이지 정보 */}
                <div className="pagination-info">
                  총 {entries.length}개의 메시지 중 {startIndex + 1}-
                  {Math.min(endIndex, entries.length)}번째 표시
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 팝업 모달 */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            // 오버레이 자체를 클릭했을 때만 모달 닫기
            if (e.target === e.currentTarget) {
              console.log("Modal overlay clicked - closing modal");
              setShowModal(false);
              setNewEntry({ name: "", message: "", side: "" });
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 400,
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
              // iOS에서 부드러운 스크롤링 적용
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="modal-header">
              <h3>축하 메시지 남기기</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  setNewEntry({ name: "", message: "", side: "" });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="guestbook-form">
              {/* Segment control for groom/bride selection */}
              <div className="form-group">
                <label style={{ marginBottom: 8 }}>신랑/신부 선택</label>
                <div className="segment-control">
                  {segmentOptions.map((opt, index) => (
                    <button
                      type="button"
                      key={opt.value}
                      ref={index === 0 ? firstSegmentRef : null}
                      className={`segment-btn${
                        newEntry.side === opt.value ? " selected" : ""
                      }`}
                      onClick={() =>
                        setNewEntry((prev) => ({
                          ...prev,
                          side: opt.value as "groom" | "bride",
                        }))
                      }
                      tabIndex={0}
                    >
                      <span style={{ fontSize: "1.4rem" }}>{opt.emoji}</span>{" "}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Name input on its own row */}
              <div className="form-group" onClick={(e) => e.stopPropagation()}>
                <label htmlFor="name">이름</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  id="name"
                  value={newEntry.name}
                  onChange={(e) =>
                    setNewEntry((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="이름을 입력하세요"
                  maxLength={20}
                  required
                  // 모바일에서 포커스 시 자동 스크롤 개선
                  onFocus={(e) => {
                    console.log("Name input focused");
                    // iOS에서 입력 필드가 화면에 보이도록 스크롤
                    setTimeout(() => {
                      e.target.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }}
                  onTouchStart={(e) => {
                    console.log("Name input touched");
                    e.stopPropagation();
                    // 모바일에서 강제로 포커스
                    setTimeout(() => {
                      if (nameInputRef.current) {
                        nameInputRef.current.focus();
                      }
                    }, 10);
                  }}
                  onClick={(e) => {
                    console.log("Name input clicked");
                    e.stopPropagation();
                    if (nameInputRef.current) {
                      nameInputRef.current.focus();
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              </div>
              {/* Message input always visible */}
              <div className="form-group" onClick={(e) => e.stopPropagation()}>
                <label htmlFor="message">축하 메시지 (최대 3줄)</label>
                <textarea
                  ref={messageTextareaRef}
                  id="message"
                  value={newEntry.message}
                  onChange={handleMessageChange}
                  placeholder="축하의 마음을 담아 메시지를 남겨주세요"
                  maxLength={200}
                  rows={3}
                  required
                  style={{ resize: "none" }}
                  // 모바일에서 포커스 시 자동 스크롤 개선
                  onFocus={(e) => {
                    console.log("Message textarea focused");
                    // iOS에서 입력 필드가 화면에 보이도록 스크롤
                    setTimeout(() => {
                      e.target.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }}
                  onTouchStart={(e) => {
                    console.log("Message textarea touched");
                    e.stopPropagation();
                    // 모바일에서 강제로 포커스
                    setTimeout(() => {
                      if (messageTextareaRef.current) {
                        messageTextareaRef.current.focus();
                      }
                    }, 10);
                  }}
                  onClick={(e) => {
                    console.log("Message textarea clicked");
                    e.stopPropagation();
                    if (messageTextareaRef.current) {
                      messageTextareaRef.current.focus();
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                />
                <span className="char-count">
                  {newEntry.message.length}/200
                </span>
              </div>
              <button
                type="submit"
                disabled={
                  loading || !newEntry.name.trim() || !newEntry.message.trim()
                }
                className="submit-btn"
                style={{ marginTop: 16 }}
              >
                {loading ? "전송 중..." : "메시지 남기기"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default GuestbookSection;
